import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Brain, Upload, Send, Loader, Lightbulb, FileText,
  Plus, Trash2, BookOpen, MessageSquare, Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const KnowledgeBrain = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.knowledge;
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  const [view, setView] = useState('home');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');

  
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Knowledge Brain', lastAssistant);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadDocs(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadDocs = async () => {
    try { const res = await axios.get(`${API}/compass/knowledge/docs`, { headers }); setDocs(res.data.documents); } catch (e) { console.error(e); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/compass/knowledge/upload-doc?language=${lang}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      loadDocs();
    } catch (e) { alert('Upload failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  const addText = async () => {
    if (!((textTitle) == null ? '' : String(textTitle)).trim() || !((textContent) == null ? '' : String(textContent)).trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/compass/knowledge/add-text`, { title: textTitle, content: textContent, language: lang }, { headers });
      setTextTitle(''); setTextContent(''); loadDocs();
    } catch (e) { alert('Failed to add document.'); }
    finally { setLoading(false); }
  };

  const deleteDoc = async (docId) => {
    try { await axios.delete(`${API}/compass/knowledge/doc/${docId}`, { headers }); loadDocs(); } catch (e) { console.error(e); }
  };

  const askQuestion = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || sending) return;
    setView('chat');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput(''); setSending(true);
    try {
      const res = await axios.post(`${API}/compass/knowledge/ask`, { question: text, session_id: sessionId, language: lang }, { headers });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.answer }]);
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'Error. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
    }
    finally { setSending(false); }
  };

  const summarizeDoc = async (docId, filename) => {
    setView('chat'); setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: `Summarize: ${filename}` }]);
    try {
      const res = await axios.post(`${API}/compass/knowledge/summarize/${docId}`, { language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.summary }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Summarization failed. Please try again.' : 'Samenvatting mislukt. Probeer het opnieuw.' }]); }
    finally { setSending(false); }
  };

  return (
    <div className="compass-coach-page" data-testid="knowledge-page">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-purple-200)' }}><Brain size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>
        <nav className="compass-sidebar-nav">
          <button className={`compass-nav-item ${view === 'home' ? 'active' : ''}`} onClick={() => setView('home')}><BookOpen size={16} /> {tp.documents}</button>
          <button className={`compass-nav-item ${view === 'chat' ? 'active' : ''}`} onClick={() => setView('chat')}><MessageSquare size={16} /> {lang === 'nl' ? 'Stel Vragen' : 'Ask Questions'}</button>
        </nav>
        <div className="compass-role-badge" style={{ background: 'var(--bg-section)', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
          <span className="caption" style={{ fontWeight: 600 }}>{lang === 'nl' ? 'KENNISBANK' : 'KNOWLEDGE BASE'}</span>
          <span className="body-small">{docs.length} {lang === 'nl' ? 'document' : 'document'}{docs.length !== 1 ? (lang === 'nl' ? 'en' : 's') : ''}</span>
        </div>
      </aside>

      <main className="compass-main">
        {view === 'home' && (
          <div className="explorer-dashboard-content" style={{ maxWidth: '800px' }}>
            {/* Upload section */}
            <div className="auto-input-card" data-testid="upload-section">
              <div className="auto-input-header"><Upload size={20} /><h3 className="heading-3">{lang === 'nl' ? 'Voeg Bedrijfskennis Toe' : 'Add Company Knowledge'}</h3></div>
              <div className="explorer-dropzone" style={{ padding: '1.5rem', margin: '1rem 0' }} onClick={() => fileRef.current?.click()} data-testid="doc-dropzone">
                <Upload size={24} />
                <span className="body-small">{lang === 'nl' ? 'Upload .txt, .md, .csv of .pdf bestanden' : 'Upload .txt, .md, .csv, or .pdf files'}</span>
                <input ref={fileRef} type="file" accept=".txt,.md,.csv,.pdf" style={{ display: 'none' }} onChange={(e) => uploadDoc(e.target.files[0])} />
              </div>
              <div style={{ textAlign: 'center', margin: '0.5rem 0', color: 'var(--text-secondary)' }}>{lang === 'nl' ? '— of typ direct —' : '— or type directly —'}</div>
              <input type="text" className="form-input" placeholder={lang === 'nl' ? 'Document titel' : 'Document title'} value={textTitle} onChange={(e) => setTextTitle(e.target.value)} style={{ marginBottom: '0.5rem' }} data-testid="text-title" />
              <textarea className="form-input auto-textarea" placeholder={lang === 'nl' ? 'Plak procedures, handleidingen, beleid, FAQ...' : 'Paste procedures, guides, policies, FAQ...'} value={textContent} onChange={(e) => setTextContent(e.target.value)} rows={3} data-testid="text-content" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '0.75rem' }} onClick={addText} disabled={!((textTitle) == null ? '' : String(textTitle)).trim() || !((textContent) == null ? '' : String(textContent)).trim() || loading} data-testid="add-text-btn">
                {loading ? <Loader className="spin" size={16} /> : <Plus size={16} />} {lang === 'nl' ? 'Toevoegen aan Kennisbank' : 'Add to Knowledge Base'}
              </button>
            </div>

            {/* Document list */}
            {docs.length > 0 && (
              <div data-testid="doc-list">
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{lang === 'nl' ? 'Opgeslagen Documenten' : 'Stored Documents'}</h3>
                {(Array.isArray(docs) ? docs : []).map(d => (
                  <div key={d.doc_id} className="explorer-history-item" style={{ cursor: 'default' }}>
                    <FileText size={18} />
                    <div style={{ flex: 1 }}>
                      <span className="body-small" style={{ fontWeight: 500 }}>{d.filename}</span>
                      <span className="caption">{d.content_length} {lang === 'nl' ? 'tekens' : 'chars'} · {d.file_type}</span>
                    </div>
                    <button className="btn-secondary" style={{ padding: '0.3rem 0.6rem', fontSize: '0.7rem' }} onClick={() => summarizeDoc(d.doc_id, d.filename)}>{lang === 'nl' ? 'Samenvatten' : 'Summarize'}</button>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => deleteDoc(d.doc_id)}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Quick question */}
            <div className="auto-input-card" data-testid="quick-ask">
              <div className="auto-input-header"><Search size={20} /><h3 className="heading-3">{lang === 'nl' ? 'Vraag je Kennisbank' : 'Ask Your Knowledge Base'}</h3></div>
              <div className="compass-suggestions" style={{ justifyContent: 'flex-start', marginTop: '0.75rem' }} data-testid="kb-suggestions">
                {(lang === 'nl' ? ["Hoe verwerken we retouren?", "Wat is onze factuurprocedure?", "Vat alle bedrijfsbeleid samen"] : ["How do we process returns?", "What is our invoice procedure?", "Summarize all company policies"]).map((s, i) => (
                  <button key={i} className="compass-suggestion-btn" onClick={() => askQuestion(s)} data-testid={`kb-sug-${i}`}><Lightbulb size={14} /> {s}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {view === 'chat' && (
          <div className="compass-chat-container">
            <div className="compass-chat-messages" data-testid="kb-messages">
              {messages.length === 0 && (
                <div className="compass-chat-welcome">
                  <Brain size={40} />
                  <h2 className="heading-2">{lang === 'nl' ? 'Vraag alles over je bedrijf' : 'Ask anything about your company'}</h2>
                  <p className="body-medium text-secondary">{lang === 'nl' ? 'Ik doorzoek je kennisbank en geef je antwoorden gebaseerd op je documenten.' : 'I\'ll search your knowledge base and give you answers based on your documents.'}</p>
                  <div className="compass-suggestions">
                    {(lang === 'nl' ? ["Hoe gaan we om met productretouren?", "Wat zijn onze betalingsvoorwaarden?", "Beschrijf het onboardingproces"] : ["How do we handle product returns?", "What are our payment terms?", "Describe the onboarding process"]).map((s, i) => (
                      <button key={i} className="compass-suggestion-btn" onClick={() => askQuestion(s)}><Lightbulb size={14} /> {s}</button>
                    ))}
                  </div>
                </div>
              )}
              {(Array.isArray(messages) ? messages : []).map((msg, i) => (
                <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                  {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
                </div>
              ))}
              {sending && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {lang === 'nl' ? 'Kennisbank doorzoeken...' : 'Searching knowledge base...'}</div></div>}
              <div ref={chatEndRef} />
            </div>
            <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); askQuestion(input); }}>
              <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.askPlaceholder} disabled={sending} className="form-input" data-testid="kb-input" />
              <button type="submit" disabled={sending || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default KnowledgeBrain;
